import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  MessageSquare,
  AtSign,
  ShoppingBag,
  Shield,
  Zap,
  Globe,
  Clock,
  Lock,
  CreditCard,
  Users,
  CheckCircle2,
  Star,
  Tag,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { serviceSlug } from "@/lib/service-slugs";
import { useI18n } from "@/lib/i18n";
import { SiteFooter } from "@/components/site-footer";
import { TopBar } from "@/components/top-bar";

export function Landing() {
  const { user } = useAuth();
  const { t } = useI18n();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[600px] bg-gradient-hero"
        aria-hidden
      />
      <TopBar />
      <main className="relative mx-auto max-w-6xl px-6 pt-16 pb-24 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Zap className="h-3.5 w-3.5" /> {t("app.tagline")}
          </div>
          <h1 className="mt-6 text-5xl font-bold tracking-tight text-foreground sm:text-7xl">
            {t("landing.hero_title")}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground sm:text-xl">{t("landing.hero_sub")}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {user ? (
              <Button asChild size="lg" className="bg-gradient-primary shadow-glow">
                <Link to="/dashboard">
                  {t("landing.cta")} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" className="bg-gradient-primary shadow-glow">
                  <Link to="/auth" search={{ tab: "register" }}>
                    Get Started <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/auth" search={{ tab: "login" }}>
                    {t("auth.login")}
                  </Link>
                </Button>
              </>
            )}
          </div>
          <dl className="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-6 sm:grid-cols-4">
            {[
              { k: "50k+", v: t("landing.stats.users") },
              { k: "200+", v: t("landing.stats.services") },
              { k: "30s", v: t("landing.stats.delivery") },
              { k: "4.8★", v: t("landing.stats.rating") },
            ].map((s) => (
              <div
                key={s.v}
                className="rounded-xl border border-border/60 bg-card/40 p-3 backdrop-blur"
              >
                <dt className="text-2xl font-bold text-foreground">{s.k}</dt>
                <dd className="text-xs text-muted-foreground">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <section aria-labelledby="features-heading" className="mt-24">
          <h2 id="features-heading" className="sr-only">
            Core services
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: MessageSquare,
                title: t("landing.core.sms.title"),
                desc: t("landing.core.sms.desc"),
              },
              {
                icon: AtSign,
                title: t("landing.core.email.title"),
                desc: t("landing.core.email.desc"),
              },
              {
                icon: ShoppingBag,
                title: t("landing.core.accounts.title"),
                desc: t("landing.core.accounts.desc"),
              },
            ].map((f) => (
              <div
                key={f.title}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-elegant transition-all hover:border-primary/40 hover:shadow-glow"
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-primary shadow-glow">
                  <f.icon className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" /> {t("landing.secure")}
          </span>
          <span className="inline-flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" /> {t("landing.instant")}
          </span>
          <span className="inline-flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" /> {t("landing.worldwide")}
          </span>
        </div>

        <section aria-labelledby="how-heading" className="mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="how-heading" className="text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.how.title")}
            </h2>
            <p className="mt-3 text-muted-foreground">{t("landing.how.subtitle")}</p>
          </div>
          <ol className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: CreditCard,
                title: t("landing.how.step1.title"),
                desc: t("landing.how.step1.desc"),
              },
              {
                icon: MessageSquare,
                title: t("landing.how.step2.title"),
                desc: t("landing.how.step2.desc"),
              },
              {
                icon: CheckCircle2,
                title: t("landing.how.step3.title"),
                desc: t("landing.how.step3.desc"),
              },
            ].map((s) => (
              <li
                key={s.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-elegant"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-foreground">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="use-cases-heading" className="mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="use-cases-heading" className="text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.usecases.title")}
            </h2>
            <p className="mt-3 text-muted-foreground">{t("landing.usecases.subtitle")}</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {[
              {
                icon: Lock,
                title: t("landing.usecases.privacy.title"),
                desc: t("landing.usecases.privacy.desc"),
              },
              {
                icon: Users,
                title: t("landing.usecases.manage.title"),
                desc: t("landing.usecases.manage.desc"),
              },
              {
                icon: Clock,
                title: t("landing.usecases.queues.title"),
                desc: t("landing.usecases.queues.desc"),
              },
              {
                icon: Star,
                title: t("landing.usecases.social.title"),
                desc: t("landing.usecases.social.desc"),
              },
            ].map((u) => (
              <div
                key={u.title}
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-elegant"
              >
                <div className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <u.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{u.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="services-heading" className="mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="services-heading" className="text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.services.title")}
            </h2>
            <p className="mt-3 text-muted-foreground">{t("landing.services.subtitle")}</p>
          </div>
          {/* Popular services — clickable links to pSEO pages */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {(
              [
                { code: "wa", name: "WhatsApp" },
                { code: "tg", name: "Telegram" },
                { code: "ds", name: "Discord" },
                { code: "ig", name: "Instagram" },
                { code: "fb", name: "Facebook" },
                { code: "tw", name: "X (Twitter)" },
                { code: "lf", name: "TikTok" },
                { code: "nf", name: "Netflix" },
                { code: "oi", name: "Tinder" },
                { code: "mo", name: "Bumble" },
                { code: "go", name: "Google" },
                { code: "wx", name: "Apple" },
                { code: "am", name: "Amazon" },
                { code: "ub", name: "Uber" },
                { code: "uk", name: "Airbnb" },
                { code: "ts", name: "PayPal" },
                { code: "ya", name: "Yandex" },
              ] as const
            ).map((s) => (
              <Link
                key={s.code}
                to="/$lang/sms/$service"
                params={{ lang: "en", service: serviceSlug(s.code) }}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Public pricing preview section */}
        <section aria-labelledby="pricing-heading" className="mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
              <Tag className="h-3.5 w-3.5" /> Pay-as-you-go
            </div>
            <h2 id="pricing-heading" className="text-3xl font-bold tracking-tight sm:text-4xl">
              Transparent Pricing
            </h2>
            <p className="mt-3 text-muted-foreground">
              No subscription. No hidden fees. Pay only when a code arrives.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                { code: "wa", name: "WhatsApp", price: 0.35 },
                { code: "tg", name: "Telegram", price: 0.35 },
                { code: "ds", name: "Discord", price: 0.35 },
                { code: "ig", name: "Instagram", price: 0.37 },
                { code: "go", name: "Google", price: 0.4 },
                { code: "fb", name: "Facebook", price: 0.35 },
              ] as const
            ).map((s) => (
              <Link
                key={s.code}
                to="/$lang/sms/$service"
                params={{ lang: "en", service: serviceSlug(s.code) }}
                className="group flex items-center justify-between rounded-2xl border border-border bg-card p-5 shadow-elegant transition-all hover:border-primary/40 hover:shadow-glow"
              >
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">
                    {s.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">SMS verification number</p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-primary">from ${s.price.toFixed(2)}</span>
                  <ArrowRight className="ml-auto mt-1 h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/$lang/pricing"
              params={{ lang: "en" }}
              className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-5 py-2.5 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
            >
              View all prices &amp; services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        <section aria-labelledby="faq-heading" className="mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="faq-heading" className="text-3xl font-bold tracking-tight sm:text-4xl">
              {t("landing.faq.title")}
            </h2>
          </div>
          <div className="mx-auto mt-10 max-w-3xl space-y-4">
            {[
              { q: t("landing.faq.q1"), a: t("landing.faq.a1") },
              { q: t("landing.faq.q2"), a: t("landing.faq.a2") },
              { q: t("landing.faq.q3"), a: t("landing.faq.a3") },
              { q: t("landing.faq.q4"), a: t("landing.faq.a4") },
              { q: t("landing.faq.q5"), a: t("landing.faq.a5") },
              { q: t("landing.faq.q6"), a: t("landing.faq.a6") },
            ].map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-border bg-card p-5 shadow-elegant open:shadow-glow"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-base font-semibold text-foreground">
                  {f.q}
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section aria-labelledby="cta-heading" className="mt-24">
          <div className="mx-auto max-w-3xl rounded-3xl border border-primary/30 bg-gradient-primary p-10 text-center shadow-glow">
            <h2
              id="cta-heading"
              className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl"
            >
              {t("landing.cta.title")}
            </h2>
            <p className="mt-3 text-primary-foreground/90">{t("landing.cta.subtitle")}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" variant="secondary">
                <Link
                  to={user ? "/dashboard" : "/auth"}
                  search={user ? undefined : { tab: "register" }}
                >
                  {user ? t("landing.cta") : t("landing.cta.create_account")}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
